import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../utils/api";
import styles from "./NewRolePage.module.scss";
import { AxiosResponse, AxiosErrorType } from "../../../src/types";

interface Organization {
  id: string;
  name: string;
}

interface FormValues {
  name: string;
  permissions: Record<string, boolean>;
  organizationid: string;
}

const NewOrgRolePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orgId = queryParams.get("org_id") || "";
  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    permissions: {},
    organizationid: orgId,
  });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await api.get<AxiosResponse<Organization[]>>("/core/organization/list");
        setOrganizations(response.data);
      } catch (err: any) {
        setError(err.message || "Failed to fetch organizations");
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizations();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setFormValues((prev) => ({
        ...prev,
        permissions: JSON.parse(e.target.value || "{}"),
      }));
      setError("");
    } catch (err) {
      setError("Invalid JSON format for permissions");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const roleTypeResponse = await api.post("/core/role/orgroletype/create", {
        name: formValues.name,
        permissions: formValues.permissions,
        organization_id: formValues.organizationid || null,
      });
      console.log("Organization role type created:", roleTypeResponse.data);

      if (formValues.organizationid) {
        const assignmentResponse = await api.post("/core/role/assign", {
          organization_id: formValues.organizationid,
          orgroletype_id: roleTypeResponse.data.role.id,
        });
        console.log("Organization role assigned:", assignmentResponse.data);
      }

      navigate("/roles");
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to create organization role type";
      setError(errorMessage);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <h2>Add New Organization Role Type</h2>
      {error && <div className={styles.error}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="name">Role Type Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="organizationid">Assign to Organization (Optional)</label>
          <select
            id="organizationid"
            name="organizationid"
            value={formValues.organizationid}
            onChange={handleInputChange}
          >
            <option value="">None</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="permissions">Permissions (JSON)</label>
          <input
            type="text"
            id="permissions"
            name="permissions"
            value={JSON.stringify(formValues.permissions)}
            onChange={handlePermissionsChange}
            placeholder='{"view": true, "edit": false}'
            className={styles.editableInput}
          />
        </div>
        <button type="submit">Create Organization Role Type</button>
      </form>
    </div>
  );
};

export default NewOrgRolePage;
